import React, { Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AppProvider } from "./context/AppContext";
import Header from "./components/Header";
import DiscountBanner from "./components/DiscountBanner";
import Footer from "./components/Footer";
import CartSidebar from "./components/CartSidebar";
import ScrollToTop from "./components/ScrollToTop";

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
  </div>
);

// Pages - Code split with lazy loading
const HomePage = React.lazy(
  () => import(/* webpackChunkName: "main.home_page" */ "./pages/HomePage"),
);
const AyurbotPage = React.lazy(
  () =>
    import(/* webpackChunkName: "main.ayurbot_page" */ "./pages/AyurbotPage"),
);
const ProductsPage = React.lazy(
  () =>
    import(/* webpackChunkName: "main.products_page" */ "./pages/ProductsPage"),
);
const ProductDetailPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "main.product_detail_page" */ "./pages/ProductDetailPage"
    ),
);
const AboutPage = React.lazy(
  () => import(/* webpackChunkName: "main.about_page" */ "./pages/AboutPage"),
);
const FranchisePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "main.franchise_page" */ "./pages/FranchisePage"
    ),
);
const ServicesPage = React.lazy(
  () =>
    import(/* webpackChunkName: "main.services_page" */ "./pages/ServicesPage"),
);
const ContactPage = React.lazy(
  () =>
    import(/* webpackChunkName: "main.contact_page" */ "./pages/ContactPage"),
);
const CheckoutPage = React.lazy(
  () =>
    import(/* webpackChunkName: "main.checkout_page" */ "./pages/CheckoutPage"),
);
const OrdersPage = React.lazy(
  () => import(/* webpackChunkName: "main.orders_page" */ "./pages/OrdersPage"),
);
const OrderConfirmationPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "main.order_confirmation_page" */ "./pages/OrderConfirmationPage"
    ),
);
const OrderUsagePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "main.order_usage_page" */ "./pages/OrderUsagePage"
    ),
);
const PrivacyPage = React.lazy(() =>
  import(
    /* webpackChunkName: "main.privacy_page" */ "./pages/PolicyPages"
  ).then((m) => ({ default: m.PrivacyPage })),
);
const ShippingPage = React.lazy(() =>
  import(
    /* webpackChunkName: "main.shipping_page" */ "./pages/PolicyPages"
  ).then((m) => ({ default: m.ShippingPage })),
);
const TermsPage = React.lazy(() =>
  import(/* webpackChunkName: "main.terms_page" */ "./pages/PolicyPages").then(
    (m) => ({ default: m.TermsPage }),
  ),
);
const RefundsPage = React.lazy(() =>
  import(
    /* webpackChunkName: "main.refunds_page" */ "./pages/PolicyPages"
  ).then((m) => ({ default: m.RefundsPage })),
);
const LoginPage = React.lazy(
  () =>
    import(/* webpackChunkName: "main.login_page" */ "./pages/LoginPage"),
);
const AdminPage = React.lazy(
  () =>
    import(/* webpackChunkName: "main.admin_page" */ "./pages/AdminPage"),
);
const AdminLoginPage = React.lazy(
  () =>
    import(/* webpackChunkName: "main.admin_login_page" */ "./pages/AdminLoginPage"),
);

function AppLayout() {
  const location = useLocation();
  const isLoginPage = location.pathname === "/login";
  const isAdminLoginPage = location.pathname === "/admin-login";
  const isAdminPage = location.pathname === "/admin" || location.pathname.startsWith("/admin/");
  const isAuthPage = isLoginPage || isAdminLoginPage || isAdminPage;

  return (
    <div className="flex flex-col min-h-screen">
      {!isAuthPage && <Header />}
      {!isAuthPage && <DiscountBanner />}
      <main
        className={`flex-1 w-full ${
          isAdminPage ? "" : isLoginPage || isAdminLoginPage ? "bg-gradient-to-br from-slate-50 to-slate-100" : "pt-32 bg-white/50"
        }`}
      >
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/ayurbot" element={<AyurbotPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/products/:id" element={<ProductDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/franchise" element={<FranchisePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route
              path="/order-confirmation/:orderId"
              element={<OrderConfirmationPage />}
            />
            <Route
              path="/order-usage/:orderId"
              element={<OrderUsagePage />}
            />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin-login" element={<AdminLoginPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/shipping" element={<ShippingPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/refunds" element={<RefundsPage />} />
          </Routes>
        </Suspense>
      </main>
      {!isAuthPage && <Footer />}
      {!isAuthPage && <CartSidebar />}
    </div>
  );
}

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppLayout />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
